import { useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import useOrder from 'hooks/Order/useOrder'
import type { LoyaltyEsimUpsellTicketMetadata } from 'loyalty/constants/loyaltyEsimUpsellOptions'
import {
  getEsimPurchasedUpsellFromOrderBenefit,
  getEsimUpsellTicketIdFromIncludedGb,
} from 'loyalty/lib/esims/esimUpsellTicketUtils'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import {
  getEsimGbForTier,
  isPastEsimTripEnd,
  isWithinEsimRedemptionWindow,
} from 'loyalty/lib/esims/loyaltyEsimUtils'
import getLoyaltyAccountTier from 'loyalty/selectors/getLoyaltyAccountTier'

export interface LoyaltyOrderEsimIncludedBenefit {
  tier: App.LoyaltyTier
  includedGb: number
  isHistoricalTier: boolean
}

interface LoyaltyOrderEsimBenefitView {
  esimBenefit: App.OrderLoyaltyEsimBenefit
  includedBenefit: LoyaltyOrderEsimIncludedBenefit
  purchasedUpsell?: LoyaltyEsimUpsellTicketMetadata
  currentPackageTicketId: App.LoyaltyEsimUpsellTicketId
  departureDate?: Date
  endDate?: Date
  isWithinRedemptionWindow: boolean
  tripEnded: boolean
}

function getLoyaltyOrderViewEsimData(
  order: App.Order,
  loyaltyConfig: App.LoyaltyConfig,
  currentTier: App.LoyaltyTier | undefined,
): LoyaltyOrderEsimBenefitView | undefined {
  const esimBenefit = order.loyaltyDetails?.esimBenefit
  if (!esimBenefit) return undefined

  const includedGb = getEsimGbForTier(loyaltyConfig, esimBenefit.tier)
  if (includedGb === undefined) return undefined

  const purchasedUpsell = getEsimPurchasedUpsellFromOrderBenefit(esimBenefit, loyaltyConfig)
  const currentPackageTicketId = purchasedUpsell?.ticketId
    ?? getEsimUpsellTicketIdFromIncludedGb(includedGb)

  if (!currentPackageTicketId) return undefined

  const departureDate = esimBenefit.departureDate ? new Date(esimBenefit.departureDate) : undefined
  const endDate = esimBenefit.endDate ? new Date(esimBenefit.endDate) : undefined

  return {
    esimBenefit,
    includedBenefit: {
      tier: esimBenefit.tier,
      includedGb,
      isHistoricalTier: !!currentTier && esimBenefit.tier !== currentTier,
    },
    purchasedUpsell,
    currentPackageTicketId,
    departureDate,
    endDate,
    isWithinRedemptionWindow: isWithinEsimRedemptionWindow(departureDate, endDate),
    tripEnded: isPastEsimTripEnd(endDate),
  }
}

/**
 * Interprets an order's eSIM benefit for loyalty UI: included allowance, purchased upsell, and trip timing.
 */
export default function useLoyaltyOrderViewEsimData(
  orderId?: string,
): LoyaltyOrderEsimBenefitView | undefined {
  const [order] = useOrder(orderId)
  const currentTier = useAppSelector(getLoyaltyAccountTier)
  const [loyaltyConfig] = useLoyaltyConfig()

  return useMemo(() => {
    if (!orderId || !order || !loyaltyConfig) return undefined

    return getLoyaltyOrderViewEsimData(order, loyaltyConfig, currentTier)
  }, [currentTier, loyaltyConfig, order, orderId])
}
